import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { SEO } from "@/components/common/SEO";
import { Container } from "@/components/ui/Container";
import { Section } from "@/components/ui/Section";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { PageHero } from "@/components/sections/PageHero";
import { CTABand } from "@/components/sections/CTABand";
import { BLOG_POSTS } from "@/data/content";
import { staggerContainer, staggerItem, cardHover, viewportOnce } from "@/lib/motion";

export default function Blogs() {
  return (
    <>
      <SEO
        title="Blogs"
        description="Articles, insights, and updates from MedCore Research on clinical research site operations, recruitment, quality, and partnerships."
        path="/blogs"
      />
      <PageHero
        eyebrow="Blogs"
        title="Articles, insights & updates"
        description="Perspectives on clinical research site operations, quality, recruitment, and partnerships from the MedCore Research team."
      />

      <Section data-testid="blogs-section">
        <Container>
          <SectionHeading
            eyebrow="Blogs"
            title="Articles, Insights & Updates"
            align="left"
          />
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
          >
            {BLOG_POSTS.map((post) => (
              <motion.article
                key={post.slug}
                variants={staggerItem}
                whileHover={{ y: -4 }}
                transition={cardHover}
                className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow duration-200 hover:shadow-md"
                data-testid="blog-card"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.image}
                    alt="Clinical research professionals collaborating"
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 font-medium text-primary">
                      {post.category}
                    </span>
                    {post.date && (
                      <span className="text-muted-foreground">{post.date}</span>
                    )}
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-foreground">
                    {post.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {post.excerpt}
                  </p>
                  <Link
                    to={`/blogs/${post.slug}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-secondary"
                    aria-label={`Read ${post.title}`}
                  >
                    Read more
                    <ArrowRight
                      size={15}
                      className="transition-transform duration-200 group-hover:translate-x-0.5"
                    />
                  </Link>
                </div>
              </motion.article>
            ))}
          </motion.div>

          <p className="mt-10 text-center text-sm text-muted-foreground">
            More clinical research articles and educational resources are coming soon.
          </p>
        </Container>
      </Section>

      <CTABand />
    </>
  );
}
